import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { Gamepad2, Layers } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";

const API = `${getApiUrl()}/api/master`;

// Display order + labels for each relationship bucket.
const SECTIONS = [
  { key: "alternateVersions", label: "Alternate Versions" },
  { key: "remasteredAs", label: "Remastered as:" },
  { key: "remasterOf", label: "Remaster of:" },
  { key: "remadeAs", label: "Remade as:" },
  { key: "remakeOf", label: "Remake of:" },
  { key: "prequels", label: "Prequels:" },
  { key: "prequelOf", label: "Prequel of:" },
  { key: "sequels", label: "Sequels:" },
  { key: "sequelOf", label: "Sequel of:" },
  { key: "spinoffs", label: "Spin-offs:" },
  { key: "spinoffOf", label: "Spin-off of:" },
  { key: "containedInCollections", label: "Contained within this collection:" },
  { key: "collectionContains", label: "Contains versions of these past games:" },
];

const relTarget = (g) =>
  (g.versionCount > 1 && g.versionGroupId)
    ? `/browse/version/${g.versionGroupId}`
    : `/browse/${g.id}`;

function RelGameCard({ g }) {
  const yr = (g.releaseDate || "").match(/\d{4}/)?.[0];
  const sys = g.primarySystem || (g.systems && g.systems[0]);
  return (
    <Link
      to={relTarget(g)}
      data-testid={`related-game-${g.id}`}
      className="flex gap-2 p-2 rounded-lg bg-[#121212] border border-white/5 hover:border-cyan-500/40 transition-colors group w-full sm:w-[280px]"
    >
      <div className="w-12 h-16 shrink-0 rounded overflow-hidden bg-black/50">
        {g.boxArtUrl ? (
          <img src={g.boxArtUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Gamepad2 className="w-5 h-5 text-white/20" /></div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm leading-tight line-clamp-2 group-hover:text-cyan-300 transition-colors">
          {g.name}{yr ? <span className="text-white/40 font-normal"> ({yr})</span> : null}
        </div>
        <div className="flex flex-wrap items-center gap-1 mt-1">
          {(g.platforms || []).slice(0, 2).map(p => <PlatformBadge key={p} platform={p} size="sm" />)}
          {sys && <SystemBadge system={sys} size="sm" />}
          {g.versionCount > 1 && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 flex items-center gap-0.5">
              <Layers className="w-2 h-2" />{g.versionCount}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {g.inLibrary && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 font-medium">
              In Library{g.libraryCount > 1 ? ` ×${g.libraryCount}` : ""}
            </span>
          )}
          {g.owned && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/30 font-medium">
              Owned{g.ownedCount > 1 ? ` ×${g.ownedCount}` : ""}
            </span>
          )}
          {g.inWishlist && !g.inLibrary && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-medium">Wishlist</span>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * Renders typed related-games sections (Alternate / Remaster / Remake / Collection).
 * Pass `relations` directly (Version Hub already fetches it) OR `gameId` to fetch.
 */
export default function RelatedGames({ gameId, relations: relationsProp }) {
  const [relations, setRelations] = useState(relationsProp || null);

  useEffect(() => {
    if (relationsProp) { setRelations(relationsProp); return; }
    if (!gameId) return;
    let active = true;
    axios.get(`${API}/browse/game-relations/${gameId}`)
      .then(r => { if (active) setRelations(r.data); })
      .catch(() => { if (active) setRelations(null); });
    return () => { active = false; };
  }, [gameId, relationsProp]);

  if (!relations) return null;
  const hasAny = SECTIONS.some(s => (relations[s.key] || []).length > 0);
  if (!hasAny) return null;

  return (
    <Card className="bg-[#0A0A0A] border-white/5" data-testid="related-games">
      <CardContent className="p-4 space-y-4">
        <h3 className="text-white font-semibold text-base flex items-center gap-2">
          <Layers className="w-4 h-4 text-cyan-400" />
          Related Games
        </h3>
        {SECTIONS.map(({ key, label }) => {
          const items = relations[key] || [];
          if (items.length === 0) return null;
          return (
            <div key={key} data-testid={`related-section-${key}`}>
              <p className="text-cyan-300/80 text-xs uppercase tracking-wider font-medium mb-2">{label}</p>
              <div className="flex flex-wrap gap-2">
                {items.map(g => <RelGameCard key={g.id} g={g} />)}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
